import { createSlice } from '@reduxjs/toolkit';
import { validateModel, validateFormField } from '../validation/financialDetails';
import { canProceed } from '../utils/validation/canProceed';
import { pathOr } from 'ramda';

const initialState = {
  validation: {},
  hasTriedToSave: false,
  isDirty: false,
  isSaving: false,
  isLoading: false,
  canContinue: false,
  model: {
    grossAnnualIncome: '',
    netMonthlyIncome: '',
    financialCircumstances: '',
    mortgageExpense: '',
    earnIncome: ''
  }
};

const financialDetails = createSlice({
  name: 'financialDetails',
  initialState,
  reducers: {
    loadFinancialDetails: (state) => {
      state.isLoading = true;
    },
    loadFinancialDetailsCompleted: (state, action) => {
      state.model = action.payload;
      state.isLoading = false;
    },
    loadFinancialDetailsRejected: (state) => {
      state.isLoading = false;
    },
    modelChange: (state, action) => {
      state.model = action.payload;
      state.isDirty = true;
    },
    fieldChange: (state, { payload: { field, value, section } }) => {
      if (section) {
        state.model[section][field] = value;
      } else {
        state.model[field] = value;
      }
      state.isDirty = true;
    },
    validate: (state, action) => {
      state.validation = validateModel(action.payload);
      state.canContinue = canProceed(state.validation);
    },
    validateForReview: (state, action) => {
      state.validation = validateModel(action.payload);
    },
    validateField: (state, action) => {
      const { field, value } = action.payload;

      state.validation[field] = validateFormField(field, value);
      state.canContinue = canProceed(state.validation);
    },
    resetValidation: (state) => {
      state.validation = initialState.validation;
      state.canContinue = false;
    },
    save: (state) => {
      state.isSaving = true;
      state.hasTriedToSave = true;
    },
    saveCompleted: (state) => {
      state.isSaving = false;
    },
    saveCancelled: (state) => {
      state.isSaving = false;
    },
    saveRejected: (state) => {
      state.isSaving = false;
    },
    resetState: () => initialState,
    updateFinancialDetails: (state, action) => {
      const { financial_details } = action.payload;
      state.model = {
        ...state.model,
        mortgageExpense: pathOr(
          state.model.mortgageExpense,
          ['expenditure', 'mortgage', 'net_amount'],
          financial_details
        ),
        grossAnnualIncome: pathOr(
          state.model.grossAnnualIncome,
          ['income', 'gross_annual'],
          financial_details
        ),
        netMonthlyIncome: pathOr(
          state.model.netMonthlyIncome,
          ['income', 'regular', 'net_amount'],
          financial_details
        ),
        financialCircumstances: pathOr(
          state.model.financialCircumstances,
          ['income', 'regular', 'frequency'],
          financial_details
        )
          .replace(/_/g, ' ')
          .toUpperCase()
      };
    }
  }
});

export function tryToContinue(model, callback) {
  return (dispatch) => {
    dispatch(validate(model));
    dispatch(checkCanContinue(callback));
  };
}

function checkCanContinue(callback) {
  return (dispatch, getState) => {
    const { canContinue } = getState().financialDetails;
    if (canContinue) {
      dispatch(resetValidation());
      if (typeof callback === 'function') {
        callback();
      }
    }
  };
}

export function confirmChanges(editState, callback) {
  return (dispatch) => {
    dispatch(validate(editState));
    dispatch(confirmModelChanges(editState, callback));
  };
}

function confirmModelChanges(editState, callback) {
  return (dispatch, getState) => {
    const { canContinue } = getState().financialDetails;
    if (canContinue) {
      dispatch(modelChange(editState));
      if (typeof callback == 'function') {
        callback();
      }
    }
  };
}

export { financialDetails };
export const {
  fieldChange,
  loadFinancialDetails,
  updateFinancialDetails,
  loadFinancialDetailsCompleted,
  loadFinancialDetailsRejected,
  modelChange,
  resetValidation,
  save,
  saveCancelled,
  saveCompleted,
  saveRejected,
  validate,
  validateForReview,
  validateField,
  resetState
} = financialDetails.actions;
